import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';
import {
  BadmintonTiePayload,
  createBadmintonTie,
  deleteBadmintonTie,
  listBadmintonLeagueStandings,
  listBadmintonTies,
  unwrapTieResponse,
  updateBadmintonTie,
} from '../../../api/tournament/tiesService';
import { listTournamentFeatureToggles } from '../../../api/tournament/featureToggleService';
import { isSeasonFeatureEnabled } from '../seasonSetup';

interface TieRubber {
  side1Players?: unknown[];
  side2Players?: unknown[];
}

interface BadmintonTie {
  _id?: string;
  id?: string;
  eventCategory?: string;
  stage?: 'league' | 'knockout';
  knockout?: { stage?: string; index?: number };
  side1Team?: unknown;
  side2Team?: unknown;
  date?: string;
  time?: string;
  venue?: string;
  venueLong?: string;
  matches?: TieRubber[];
}

interface TieForm {
  eventCategory: string;
  stage: 'league' | 'knockout';
  knockoutStage: string;
  side1Team: string;
  side2Team: string;
  date: string;
  time: string;
  venue: string;
  venueLong: string;
  matches: Array<{ side1Players: string; side2Players: string }>;
}

const emptyForm = (): TieForm => ({
  eventCategory: '',
  stage: 'league',
  knockoutStage: '',
  side1Team: '',
  side2Team: '',
  date: '',
  time: '',
  venue: '',
  venueLong: '',
  matches: [{ side1Players: '', side2Players: '' }],
});

const normalizeSport = (sport?: string | null) => (sport || '').replace(/[\s_-]/g, '').toLowerCase();
const asId = (value: any): string => typeof value === 'string' ? value : value?._id || value?.id || '';
const displayValue = (value: any): string => typeof value === 'string' ? value : value?.name || value?.shortName || asId(value) || '—';
const splitIds = (value: string): string[] => value.split(',').map((item) => item.trim()).filter(Boolean);

const tieId = (tie: BadmintonTie) => tie._id || tie.id || '';

const readTies = (response: unknown): BadmintonTie[] => {
  const data: any = unwrapTieResponse<any>(response);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.ties)) return data.ties;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.paginatedResults)) return data.paginatedResults;
  return [];
};

const readLeagueStandings = (response: unknown): any[] => {
  const data: any = unwrapTieResponse<any>(response);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.standings)) return data.standings;
  if (Array.isArray(data?.paginatedResults)) return data.paginatedResults;
  return [];
};

const formFromTie = (tie: BadmintonTie): TieForm => ({
  eventCategory: tie.eventCategory || '',
  stage: tie.stage || 'league',
  knockoutStage: tie.knockout?.stage || '',
  side1Team: asId(tie.side1Team),
  side2Team: asId(tie.side2Team),
  date: tie.date?.slice(0, 10) || '',
  time: tie.time || '',
  venue: tie.venue || '',
  venueLong: tie.venueLong || '',
  matches: tie.matches?.length
    ? tie.matches.map((match) => ({
      side1Players: (match.side1Players || []).map(asId).filter(Boolean).join(', '),
      side2Players: (match.side2Players || []).map(asId).filter(Boolean).join(', '),
    }))
    : [{ side1Players: '', side2Players: '' }],
});

const BadmintonTiesPage: React.FC = () => {
  const { tournamentId: pathTournamentId, seasonId: pathSeasonId, ageCategoryId: pathAgeCategoryId, sport: pathSport } = useParams<{
    tournamentId?: string;
    seasonId?: string;
    ageCategoryId?: string;
    sport?: string;
  }>();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tournamentId = pathTournamentId || searchParams.get('tournamentId') || '';
  const seasonId = pathSeasonId || searchParams.get('seasonId') || '';
  const ageCategoryId = pathAgeCategoryId || searchParams.get('ageCategoryId') || '';
  const sport = pathSport || searchParams.get('sport');
  const isBadminton = normalizeSport(sport) === 'badminton';

  const [ties, setTies] = useState<BadmintonTie[]>([]);
  const [leagueStandings, setLeagueStandings] = useState<any[]>([]);
  const [leagueLoading, setLeagueLoading] = useState(false);
  const [selectedEventCategory, setSelectedEventCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTie, setEditingTie] = useState<BadmintonTie | null>(null);
  const [form, setForm] = useState<TieForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isTieFeatureEnabled, setIsTieFeatureEnabled] = useState(false);
  const [featureTogglesLoading, setFeatureTogglesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    if (!isBadminton || !seasonId) {
      setIsTieFeatureEnabled(false);
      setFeatureTogglesLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setFeatureTogglesLoading(true);
    listTournamentFeatureToggles(seasonId)
      .then((toggles) => {
        if (!cancelled) {
          setIsTieFeatureEnabled(isSeasonFeatureEnabled(toggles, 'TOURNAMENT_TIES'));
        }
      })
      .catch(() => {
        if (!cancelled) setIsTieFeatureEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setFeatureTogglesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isBadminton, seasonId]);

  const loadTies = useCallback(async () => {
    if (!isBadminton || !isTieFeatureEnabled || !tournamentId || !ageCategoryId) return;

    try {
      setLoading(true);
      setError(null);
      setTies(readTies(await listBadmintonTies(tournamentId, ageCategoryId)));
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || requestError?.message || 'Unable to load ties. Please try again.');
      setTies([]);
    } finally {
      setLoading(false);
    }
  }, [ageCategoryId, isBadminton, isTieFeatureEnabled, tournamentId]);

  useEffect(() => {
    loadTies();
  }, [loadTies]);

  const eventCategories = useMemo(
    () => Array.from(new Set(ties.map((tie) => tie.eventCategory).filter((value): value is string => Boolean(value)))),
    [ties]
  );

  useEffect(() => {
    if (!selectedEventCategory && eventCategories.length) {
      setSelectedEventCategory(eventCategories[0]);
    }
  }, [eventCategories, selectedEventCategory]);

  useEffect(() => {
    if (!tournamentId || !ageCategoryId || !selectedEventCategory) {
      setLeagueStandings([]);
      return;
    }
    setLeagueLoading(true);
    listBadmintonLeagueStandings(tournamentId, ageCategoryId, selectedEventCategory)
      .then((response) => setLeagueStandings(readLeagueStandings(response)))
      .catch(() => setLeagueStandings([]))
      .finally(() => setLeagueLoading(false));
  }, [tournamentId, ageCategoryId, selectedEventCategory]);

  const openCreate = () => {
    setEditingTie(null);
    setForm(emptyForm());
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (tie: BadmintonTie) => {
    setEditingTie(tie);
    setForm(formFromTie(tie));
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    if (!saving) setShowModal(false);
  };

  const updateForm = <K extends keyof TieForm>(key: K, value: TieForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const updateRubber = (index: number, key: 'side1Players' | 'side2Players', value: string) => {
    setForm((current) => ({
      ...current,
      matches: current.matches.map((match, matchIndex) => matchIndex === index ? { ...match, [key]: value } : match),
    }));
  };

  const validateForm = (): string | null => {
    if (!seasonId) return 'A season ID is required to create or edit a tie.';
    if (!form.eventCategory.trim() || !form.side1Team.trim() || !form.side2Team.trim() || !form.date || !form.time) {
      return 'Event category, both team IDs, date, and time are required.';
    }
    if (form.stage === 'knockout' && !form.knockoutStage.trim()) {
      return 'Enter the knockout stage.';
    }
    if (form.side1Team.trim() === form.side2Team.trim()) {
      return 'Side 1 and Side 2 must be different teams.';
    }
    if (!form.matches.length || form.matches.some((match) => !splitIds(match.side1Players).length || !splitIds(match.side2Players).length)) {
      return 'Each rubber needs at least one player ID for both sides.';
    }
    return null;
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const payload: BadmintonTiePayload = {
      tournament: tournamentId,
      tournamentSeason: seasonId,
      ageCategory: ageCategoryId,
      eventCategory: form.eventCategory.trim(),
      stage: form.stage,
      side1Team: form.side1Team.trim(),
      side2Team: form.side2Team.trim(),
      date: form.date,
      time: form.time,
      ...(form.venue.trim() ? { venue: form.venue.trim() } : {}),
      ...(form.venueLong.trim() ? { venueLong: form.venueLong.trim() } : {}),
      ...(form.stage === 'knockout' ? { knockout: { stage: form.knockoutStage.trim(), index: 0 } } : {}),
      matches: form.matches.map((match) => ({
        side1Players: splitIds(match.side1Players),
        side2Players: splitIds(match.side2Players),
      })),
    };

    try {
      setSaving(true);
      setFormError(null);
      const id = editingTie ? tieId(editingTie) : '';
      if (id) {
        await updateBadmintonTie(id, payload);
      } else {
        await createBadmintonTie(payload);
      }
      setShowModal(false);
      await loadTies();
    } catch (requestError: any) {
      setFormError(requestError?.response?.data?.message || requestError?.message || 'Unable to save the tie. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tie: BadmintonTie) => {
    const id = tieId(tie);
    if (!id || !window.confirm('Delete this tie? This cannot be undone.')) return;

    try {
      setDeletingId(id);
      setError(null);
      await deleteBadmintonTie(id);
      await loadTies();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || requestError?.message || 'Unable to delete the tie. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  if (!isBadminton) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert variant="warning" className="my-4">
            <Alert.Heading>Ties are available for Badminton only</Alert.Heading>
            <p className="mb-0">Open this page with <code>sport=Badminton</code> to manage badminton ties.</p>
          </Alert>
        </Container>
      </div>
    );
  }

  if (featureTogglesLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-muted my-4">Checking season settings…</div>
        </Container>
      </div>
    );
  }

  if (!isTieFeatureEnabled) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert variant="warning" className="my-4">
            <Alert.Heading>Ties are disabled for this season</Alert.Heading>
            <p className="mb-0">Enable the Ties setting from the season details page to manage badminton ties.</p>
          </Alert>
        </Container>
      </div>
    );
  }

  if (!tournamentId || !ageCategoryId) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert variant="danger" className="my-4">
            <Alert.Heading>Missing tie context</Alert.Heading>
            <p className="mb-0">Tournament ID and age category ID are required in the URL.</p>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <h1 className="page-title mb-1">Badminton Ties</h1>
            <p className="text-muted mb-0">Age category: <code>{ageCategoryId}</code>{seasonId && <> · Season: <code>{seasonId}</code></>}</p>
          </div>
          <Button variant="primary" onClick={openCreate} disabled={!seasonId}>
            <i className="ri-add-line me-1" aria-hidden="true" />
            Create tie
          </Button>
        </div>

        {!seasonId && <Alert variant="warning">A season ID is required to create or edit ties.</Alert>}
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Ties</h5>
            <Button variant="outline-secondary" size="sm" onClick={loadTies} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading ties…
              </div>
            ) : ties.length === 0 ? (
              <div className="text-center text-muted py-5">No ties have been created for this age category.</div>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Stage</th>
                      <th>Teams</th>
                      <th>Schedule</th>
                      <th>Venue</th>
                      <th>Rubbers</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ties.map((tie, index) => {
                      const id = tieId(tie);
                      const rubberCount = tie.matches?.length || 0;
                      return (
                        <tr key={id || `tie-${index}`}>
                          <td>{tie.eventCategory || '—'}</td>
                          <td>
                            <Badge bg={tie.stage === 'knockout' ? 'dark' : 'primary'}>
                              {tie.stage === 'knockout' ? tie.knockout?.stage || 'Knockout' : 'League'}
                            </Badge>
                          </td>
                          <td>{displayValue(tie.side1Team)} <span className="text-muted">vs</span> {displayValue(tie.side2Team)}</td>
                          <td>{tie.date || '—'}{tie.time ? ` · ${tie.time}` : ''}</td>
                          <td>{tie.venue || tie.venueLong || '—'}</td>
                          <td>{rubberCount} {rubberCount === 1 ? 'rubber' : 'rubbers'}</td>
                          <td className="text-end text-nowrap">
                            <Button variant="outline-primary" size="sm" className="me-2" onClick={() => openEdit(tie)}>
                              Edit
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(tie)} disabled={!id || deletingId === id}>
                              {deletingId === id ? 'Deleting…' : 'Delete'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Card className="mt-4">
          <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <h5 className="mb-0">League standings</h5>
            <Form.Select
              aria-label="League standings event category"
              className="w-auto"
              value={selectedEventCategory}
              onChange={(event) => setSelectedEventCategory(event.target.value)}
              disabled={!eventCategories.length}
            >
              {!eventCategories.length && <option value="">Create a tie to select an event</option>}
              {eventCategories.map((eventCategory) => <option key={eventCategory} value={eventCategory}>{eventCategory}</option>)}
            </Form.Select>
          </Card.Header>
          <Card.Body className="p-0">
            {leagueLoading ? (
              <div className="text-center py-4" role="status">Loading league standings…</div>
            ) : leagueStandings.length ? (
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead><tr><th>Team</th><th>Played</th><th>Won</th><th>Lost</th><th>Points</th></tr></thead>
                  <tbody>{leagueStandings.map((standing, index) => (
                    <tr key={asId(standing.team) || standing._id || index}>
                      <td>{displayValue(standing.team || standing.participant || standing)}</td>
                      <td>{standing.played ?? 0}</td>
                      <td>{standing.won ?? 0}</td>
                      <td>{standing.lost ?? 0}</td>
                      <td>{standing.points ?? 0}</td>
                    </tr>
                  ))}</tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                {selectedEventCategory ? 'No league standings have been initialized for this event.' : 'Select an event category to view league standings.'}
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      <Modal show={showModal} onHide={closeModal} centered size="lg">
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton={!saving}>
            <Modal.Title>{editingTie ? 'Edit tie' : 'Create tie'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Event category</Form.Label>
                  <Form.Control value={form.eventCategory} onChange={(event) => updateForm('eventCategory', event.target.value)} placeholder="e.g. Boys Singles" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Stage</Form.Label>
                  <Form.Select value={form.stage} onChange={(event) => updateForm('stage', event.target.value as TieForm['stage'])}>
                    <option value="league">League</option>
                    <option value="knockout">Knockout</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {form.stage === 'knockout' && (
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Knockout stage</Form.Label>
                    <Form.Control value={form.knockoutStage} onChange={(event) => updateForm('knockoutStage', event.target.value)} placeholder="e.g. Semi-final" />
                  </Form.Group>
                </Col>
              )}
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Side 1 team ID</Form.Label>
                  <Form.Control value={form.side1Team} onChange={(event) => updateForm('side1Team', event.target.value)} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Side 2 team ID</Form.Label>
                  <Form.Control value={form.side2Team} onChange={(event) => updateForm('side2Team', event.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Date</Form.Label>
                  <Form.Control type="date" value={form.date} onChange={(event) => updateForm('date', event.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Time</Form.Label>
                  <Form.Control type="time" value={form.time} onChange={(event) => updateForm('time', event.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Venue</Form.Label>
                  <Form.Control value={form.venue} onChange={(event) => updateForm('venue', event.target.value)} />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Venue details</Form.Label>
                  <Form.Control value={form.venueLong} onChange={(event) => updateForm('venueLong', event.target.value)} placeholder="Optional address or court detail" />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
              <h6 className="mb-0">Rubbers</h6>
              <Button type="button" variant="outline-secondary" size="sm" onClick={() => updateForm('matches', [...form.matches, { side1Players: '', side2Players: '' }])}>
                Add rubber
              </Button>
            </div>
            {form.matches.map((match, index) => (
              <Card key={index} className="mb-2 bg-light border-0">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong className="small">Rubber {index + 1}</strong>
                    {form.matches.length > 1 && (
                      <Button type="button" variant="link" size="sm" className="p-0 text-danger" onClick={() => updateForm('matches', form.matches.filter((_, matchIndex) => matchIndex !== index))}>
                        Remove
                      </Button>
                    )}
                  </div>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Control aria-label={`Rubber ${index + 1} side 1 player IDs`} value={match.side1Players} onChange={(event) => updateRubber(index, 'side1Players', event.target.value)} placeholder="Side 1 player IDs, comma separated" />
                    </Col>
                    <Col md={6}>
                      <Form.Control aria-label={`Rubber ${index + 1} side 2 player IDs`} value={match.side2Players} onChange={(event) => updateRubber(index, 'side2Players', event.target.value)} placeholder="Side 2 player IDs, comma separated" />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={closeModal} disabled={saving}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : editingTie ? 'Save changes' : 'Create tie'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default BadmintonTiesPage;
